import { AxiosResponse } from 'axios';
import apiClient from './apiClient';

export interface Payment {
  id: number;
  userId: number;
  amount: number;
  paymentMethod: string;
  paymentStatus: string;
  transactionDate: string;
  paypalOrderId?: string;
  paypalPayerId?: string;
  discountCodeId?: number;
  createdAt: string;
  updatedAt: string;
}

export interface Session {
  id: number;
  title: string;
  description?: string;
}

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}

export interface PaymentItem {
  id: number;
  paymentId: number;
  sessionId: number;
  amount: number;
  discountApplied: number;
  session?: Session;
}

export interface PaymentWithItems extends Payment {
  items: PaymentItem[];
  user?: User;
}

export interface PaymentStats {
  totalRevenue: number;
  averagePayment: number;
  totalCount: number;
  completedCount: number;
}

// Get PayPal config (for client-side SDK)
export const getPayPalConfig = async () => {
  try {
    const response: AxiosResponse<{ 
      clientId: string; 
      mode: string;
      clientToken?: string;
    }> = await apiClient.get('/api/payments/paypal/config');
    return response.data;
  } catch (error) {
    console.error('Error getting PayPal config:', error);
    throw error;
  }
};

// Get PayPal SDK URL
export const getPayPalSdkUrl = async () => {
  try {
    const response: AxiosResponse<{ sdkUrl: string; mode: string }> = await apiClient.get('/api/payments/paypal/sdk-url');
    return response.data;
  } catch (error) {
    console.error('Error getting PayPal SDK URL:', error);
    throw error;
  }
};

export interface CreatePayPalOrderResponse {
  orderId: string;
  paymentId: string;
}

// Create PayPal order
export const createPayPalOrder = async (sessionIds: number[], discountCode?: string): Promise<CreatePayPalOrderResponse> => {
  try {
    const response: AxiosResponse<CreatePayPalOrderResponse> = await apiClient.post('/api/payments/paypal/create-order', {
      sessionIds,
      discountCode: discountCode || undefined
    });
    return response.data;
  } catch (error) {
    console.error('Error creating PayPal order:', error);
    throw error;
  }
};

export interface CapturePayPalPaymentResponse {
  success: boolean;
  paymentId: string;
  orderId: string;
  message: string;
  // When true, the cart was already enrolled and NO charge occurred.
  // The UI must treat this as a benign terminal outcome, not a success.
  blocked?: boolean;
}

// Capture PayPal payment
export const capturePayPalPayment = async (orderID: string, paymentId: string): Promise<CapturePayPalPaymentResponse> => {
  try {
    console.log(`Capturing PayPal payment: orderID=${orderID}, paymentId=${paymentId}`);

    // Send both order ID and payment ID for maximum flexibility
    // The backend will figure out which one to use
    const response: AxiosResponse<CapturePayPalPaymentResponse> = await apiClient.post('/api/payments/paypal/capture', {
      orderID,
      paymentId
    });
    return response.data;
  } catch (error) {
    console.error('Error capturing PayPal payment:', error);
    throw error;
  }
};


// Get user payments
export const getUserPayments = async () => {
  try {
    const response: AxiosResponse<Payment[]> = await apiClient.get('/api/payments/user');
    return response.data;
  } catch (error) {
    console.error('Error getting user payments:', error);
    throw error;
  }
};

// Get payment details
export const getPaymentDetails = async (paymentId: number) => {
  try {
    const response: AxiosResponse<PaymentWithItems> = await apiClient.get(`/api/payments/${paymentId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting payment details:', error);
    throw error;
  }
};

// Admin: Get all payments
export const getAllPayments = async () => {
  try {
    const response: AxiosResponse<Payment[]> = await apiClient.get('/api/payments');
    return response.data;
  } catch (error) {
    console.error('Error getting all payments:', error);
    throw error;
  }
};

// Admin: Get payment statistics
export const getPaymentStats = async () => {
  try {
    const response: AxiosResponse<PaymentStats> = await apiClient.get('/api/payments/stats');
    return response.data;
  } catch (error) {
    console.error('Error getting payment statistics:', error);
    throw error;
  }
};

export interface UpdatePayPalConfigResponse {
  success: boolean;
  message: string;
}

// Admin: Update PayPal configuration
export const updatePayPalConfig = async (clientId: string, mode: 'sandbox' | 'live'): Promise<UpdatePayPalConfigResponse> => {
  try {
    const response: AxiosResponse<UpdatePayPalConfigResponse> = await apiClient.post('/api/payments/paypal/config', {
      clientId,
      mode
    });
    return response.data;
  } catch (error) {
    console.error('Error updating PayPal config:', error);
    throw error;
  }
};